import { useState, useCallback } from 'react';

interface ConfirmDialogOptions {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm?: () => void;
  onCancel?: () => void;
}

interface ConfirmDialogState {
  visible: boolean;
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm?: () => void;
  onCancel?: () => void;
}

/**
 * 确认对话框 Hook
 * 
 * 提供显示确认对话框的功能，返回对话框状态和控制函数
 * 
 * @example
 * const { dialogState, showConfirm, hideConfirm } = useConfirmDialog();
 * 
 * // 显示确认对话框
 * showConfirm({
 *   message: '确定要删除吗？',
 *   onConfirm: () => {
 *     // 确认后的操作
 *     hideConfirm();
 *   }
 * });
 */
export const useConfirmDialog = () => {
  const [dialogState, setDialogState] = useState<ConfirmDialogState>({
    visible: false,
    message: ''
  });

  const showConfirm = useCallback((options: ConfirmDialogOptions) => {
    setDialogState({
      visible: true,
      title: options.title,
      message: options.message,
      confirmText: options.confirmText,
      cancelText: options.cancelText,
      onConfirm: options.onConfirm,
      onCancel: options.onCancel
    });
  }, []);

  const hideConfirm = useCallback(() => {
    setDialogState(prev => ({
      ...prev,
      visible: false
    }));
  }, []);

  const handleConfirm = useCallback(() => {
    if (dialogState.onConfirm) {
      dialogState.onConfirm();
    }
    hideConfirm();
  }, [dialogState, hideConfirm]);

  const handleCancel = useCallback(() => {
    if (dialogState.onCancel) {
      dialogState.onCancel();
    }
    hideConfirm();
  }, [dialogState, hideConfirm]);

  return {
    dialogState,
    showConfirm,
    hideConfirm,
    handleConfirm,
    handleCancel
  };
};

